import React, { useEffect, useState } from "react";
import { InputAdornment, Menu, TextField, Tooltip } from "@mui/material";
import { AnimalNamesPtBr, AnimalsEnum } from "../AnimalBg/types";
import {
  AnimalGrid,
  AnimalOption,
  AvatarButton,
  PlayerIconSelectorContainer,
  SearchBox,
  Swatch,
  Swatches,
} from "./PlayerIconSelector.styles";
import AnimalBg from "../AnimalBg/AnimalBg";
import Icon from "../ui/Icon";
import { PLAYER_COLORS } from "../../style/tokens";

const animalOptions = Object.values(AnimalsEnum);

type PlayerIconSelectorProps = {
  onChange: (value: { color: string; animal: string }) => void;
};

const PlayerIconSelector: React.FC<PlayerIconSelectorProps> = ({
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAnimal, setSelectedAnimal] = useState<AnimalsEnum>();
  const [color, setColor] = useState<string>();
  const [search, setSearch] = useState("");

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAnimalSelect = (animal: string) => {
    setSelectedAnimal(animal as AnimalsEnum);
    handleClose();
  };

  useEffect(() => {
    const avatar = JSON.parse(localStorage.getItem("avatar") || "null");
    if (avatar?.animal && avatar.color) {
      setSelectedAnimal(avatar.animal);
      setColor(avatar.color);
    } else {
      setSelectedAnimal(
        animalOptions[Math.floor(Math.random() * animalOptions.length)],
      );
      setColor(PLAYER_COLORS[Math.floor(Math.random() * PLAYER_COLORS.length)]);
    }
  }, []);

  useEffect(() => {
    if (color && selectedAnimal) {
      onChange({ color, animal: selectedAnimal });
      localStorage.setItem(
        "avatar",
        JSON.stringify({ color, animal: selectedAnimal }),
      );
    }
  }, [color, selectedAnimal, onChange]);

  return (
    <PlayerIconSelectorContainer>
      <legend>Seu avatar *</legend>

      <AvatarButton type="button" onClick={handleClick} aria-label="Trocar bicho">
        <AnimalBg name={selectedAnimal} color={color} size="48px" circle dance />
        {!!selectedAnimal && AnimalNamesPtBr[selectedAnimal]}
      </AvatarButton>

      <Swatches role="radiogroup" aria-label="Cor">
        {PLAYER_COLORS.map((swatch) => (
          <Swatch
            key={swatch}
            type="button"
            role="radio"
            aria-checked={color === swatch}
            aria-label={swatch}
            $color={swatch}
            $selected={color === swatch}
            onClick={() => setColor(swatch)}
          >
            {color === swatch && <Icon name="check" size={16} strokeWidth={3.5} />}
          </Swatch>
        ))}
      </Swatches>

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        <SearchBox>
          <TextField
            placeholder="Pesquisar bicho"
            value={search}
            size="small"
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            fullWidth
            autoFocus
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon name="search" size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </SearchBox>
        <AnimalGrid>
          {animalOptions
            .filter((animal) =>
              `${animal},${AnimalNamesPtBr[animal]}`
                .toUpperCase()
                .includes(search.toUpperCase()),
            )
            .map((animal) => (
              <Tooltip
                key={animal}
                placement="top"
                arrow
                title={<span style={{ textTransform: "capitalize" }}>{AnimalNamesPtBr[animal]}</span>}
              >
                <AnimalOption
                  type="button"
                  $selected={animal === selectedAnimal}
                  onClick={() => handleAnimalSelect(animal)}
                >
                  <AnimalBg name={animal} color={color} size="40px" circle />
                </AnimalOption>
              </Tooltip>
            ))}
        </AnimalGrid>
      </Menu>
    </PlayerIconSelectorContainer>
  );
};

export default PlayerIconSelector;
